import React, { useMemo, useState } from 'react';
import { Rocket, Twitter, Linkedin, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

/**
 * Founding Creator "Share Your Status" card.
 *
 * Shown on CreatorDashboard only if the user is one of the first 100
 * creators. One-click pre-filled share buttons for X/Twitter and LinkedIn,
 * plus a copy-link fallback. The goal is free distribution: a founding
 * creator tweeting their badge becomes a signal that recruits more creators.
 *
 * Props:
 *   number:    1-100 (founding_creator_number)
 *   storeSlug: creator's public store slug (for link-back)
 */
export default function FoundingCreatorShareCard({ number, storeSlug }) {
  const [copied, setCopied] = useState(false);

  // Default share message — tight, specific, and with a clear benefit hook.
  // Uses "@theailedgerai" X handle; if you change your handle, update here.
  const baseStoreUrl = storeSlug
    ? `https://theailedger.ai/store/${storeSlug}`
    : 'https://theailedger.ai';

  // Per-channel UTM-tagged URLs so you can see exactly which creator (#X)
  // and which channel drove a visit in the marketplace's analytics.
  const buildTrackedUrl = (channel) => {
    const params = new URLSearchParams({
      utm_source: channel,
      utm_medium: 'founding_share',
      utm_campaign: `fc_${number}`,
    });
    return `${baseStoreUrl}?${params.toString()}`;
  };

  const xStoreUrl = buildTrackedUrl('x');
  const linkedInStoreUrl = buildTrackedUrl('linkedin');
  const manualCopyStoreUrl = buildTrackedUrl('manual');

  const shareMessage = useMemo(
    () =>
      `I'm Founding Creator #${number} on @theailedgerai — one of the first 100 builders shipping AI products with 0% platform fees for 6 months. 🚀\n\n${xStoreUrl}`,
    [number, xStoreUrl]
  );

  const linkedInText = useMemo(
    () =>
      `I just joined theailedger.ai as Founding Creator #${number} of 100 — a new curated marketplace for AI products with 0% platform fees for early creators. If you're shipping AI tools, prompts, or agents, check it out 👇`,
    [number]
  );

  const [customText, setCustomText] = useState(shareMessage);

  // X / Twitter intent URL
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    customText
  )}`;

  // LinkedIn share URL — LinkedIn doesn't support pre-filled text via URL
  // parameter the way X does; we share the store URL and let the platform
  // auto-fetch OG tags. We also copy the message to clipboard so the user
  // can paste it into the LinkedIn composer.
  const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
    linkedInStoreUrl
  )}`;

  const openLinkedIn = async () => {
    try {
      await navigator.clipboard.writeText(linkedInText);
      toast.success('Post copied — paste it into LinkedIn');
    } catch {
      // Clipboard may be blocked; still open the dialog.
    }
    window.open(linkedInUrl, '_blank', 'noopener,noreferrer');
  };

  // Copy-text fallback swaps in the `manual` UTM so pastes into iMessage,
  // Reddit, newsletters, etc. are still attributable to this creator.
  const copyMessage = async () => {
    const text = customText.replace(xStoreUrl, manualCopyStoreUrl);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Share message copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not access clipboard — long-press to copy manually');
    }
  };

  return (
    <div
      className="relative bg-gradient-to-br from-[#00F0FF]/10 via-[#00FF94]/5 to-transparent border border-[#00F0FF]/30 rounded-2xl p-5 md:p-6 mb-6 md:mb-8 overflow-hidden"
      data-testid="founding-share-card"
    >
      {/* Decorative corner glow */}
      <div className="pointer-events-none absolute -top-10 -right-10 w-40 h-40 bg-[#00F0FF]/10 blur-3xl rounded-full" />

      <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-5">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-[#00F0FF] to-[#00FF94] flex items-center justify-center">
            <Rocket className="w-5 h-5 text-black" />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold text-[#00F0FF] mb-0.5">
              Founding Creator #{number} of 100
            </p>
            <h3 className="text-lg md:text-xl font-bold text-white mb-1">
              Share your status, earn social proof
            </h3>
            <p className="text-xs md:text-sm text-neutral-400 max-w-md">
              Tell your audience you made the first 100. Every share sends traffic
              straight to your store.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 shrink-0">
          <Button
            onClick={() => window.open(twitterUrl, '_blank', 'noopener,noreferrer')}
            className="bg-black hover:bg-neutral-900 text-white border border-white/20 font-medium text-sm h-10 px-4"
            data-testid="share-twitter-btn"
          >
            <Twitter className="w-4 h-4 mr-2" />
            Share on X
          </Button>
          <Button
            onClick={openLinkedIn}
            className="bg-[#0A66C2] hover:bg-[#084a8f] text-white font-medium text-sm h-10 px-4"
            data-testid="share-linkedin-btn"
          >
            <Linkedin className="w-4 h-4 mr-2" />
            Share on LinkedIn
          </Button>
          <Button
            onClick={copyMessage}
            variant="outline"
            className="bg-transparent border-white/20 hover:bg-white/5 text-white font-medium text-sm h-10 px-4"
            data-testid="share-copy-btn"
          >
            {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
            {copied ? 'Copied' : 'Copy text'}
          </Button>
        </div>
      </div>

      {/* Editable message preview — gives the creator full control before sharing */}
      <details className="relative mt-4 group">
        <summary className="cursor-pointer text-xs text-neutral-500 hover:text-neutral-300 transition-colors select-none" data-testid="share-customize-toggle">
          Customize message →
        </summary>
        <textarea
          value={customText}
          onChange={(e) => setCustomText(e.target.value)}
          rows={3}
          maxLength={280}
          className="mt-3 w-full rounded-lg bg-black/40 border border-white/10 text-white text-sm p-3 resize-none focus:border-[#00F0FF]/50 focus:outline-none"
          data-testid="share-custom-textarea"
        />
        <div className="mt-1 text-right text-[10px] text-neutral-500">
          {customText.length}/280
        </div>
      </details>
    </div>
  );
}
